import { gql } from 'apollo-boost';
import NotFound from 'core/components/common/NotFound';
import { SettingsContext } from 'core/components/settingsContext';
import { useIdsFromUrl } from 'core/hooks/useIdsFromUrl';
import React, { useContext } from 'react';
import { GetProductDetailDataQuery, useGetProductDetailDataQuery } from 'site/graphql';
import { IProductDetailProps } from './productDetailHoc';

export const ProductFormQueryForPaymentFragment = gql`
    fragment ProductFormQueryForPaymentFragment on productForm {
        id
        type
        price
        title
        maxOrders
        showRemainingOrders
        ordersCount
        paymentStatus
        pictureId
        requiredFieldsNumber
        order {
            id
        }
        product {
            id
            name
            offer
            contentProvider {
                id
                offer
            }
        }
        webinars {
            id
            date
        }
        seminars {
            id
            date
        }
        records {
            id
            date
        }
    }
`;

gql`
    query GetProductDetailData(
        $id: Int!
        $contentProviderId: Int
        $showPublic: Boolean
        $filter: ProductFormListRequest
    ) {
        product(id: $id, contentProviderId: $contentProviderId, showPublic: $showPublic) {
            id
            name
            shortDescription
            description
            salesPoints
            warnings
            complexity
            hasAnyPurchasedForm
            hasFeedback
            tagsString
            contentProvider {
                id
                name
                offer
                code
            }
            pictureId
            lecturers {
                id
                skill
                account {
                    id
                    firstName
                    lastName
                    pictureId
                }
            }
            productForms(filter: $filter) {
                id
                title
                type
                price
                description
                paymentStatus
                requiredFieldsNumber
                maxOrders
                showRemainingOrders
                showNumberLessons
                ordersCount
                isNonSingle
                suggestedTitle
                subscriptionAccess
                suggestedProductForm {
                    ...ProductFormQueryForPaymentFragment
                }
                order {
                    id
                }
                product {
                    id
                    name
                    offer
                    contentProvider {
                        id
                        offer
                    }
                }
                withMeInSetForms {
                    id
                    title
                    type
                    subscriptionAccess
                    price
                    webinars {
                        id
                    }
                    seminars {
                        id
                    }
                    records {
                        id
                    }
                    materials {
                        id
                    }
                    certificates {
                        id
                    }
                    videoCourses {
                        id
                    }
                }
                webinars {
                    id
                    date
                    hours
                    title
                    soonCause
                }
                seminars {
                    id
                    date
                    hours
                    title
                    soonCause
                }
                records {
                    id
                    title
                    date
                }
                materials {
                    id
                    title
                }
                certificates {
                    id
                }
                videoCourses {
                    id
                    title
                }
            }
        }
    }
    ${ProductFormQueryForPaymentFragment}
`;

const productDetailFetchDataHoc = (ProductDetail: React.ComponentType<IProductDetailProps>): React.FC => () => {
    const { productId } = useIdsFromUrl('productId');
    const settings = useContext(SettingsContext);

    const { data, loading, error } = useGetProductDetailDataQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            id: productId,
            contentProviderId: settings.contentProviderId,
            showPublic: settings.coursesList.filter.showPublic,
            filter: {
                contentProviderId: settings.contentProviderId,
                showPublic: settings.coursesList.filter.showPublic,
            },
        },
    });

    const product = (data && data.product) || ({} as GetProductDetailDataQuery['product']);

    // Ошибки после SSR не кэшируются, поэтому можем отловить только само наличие ошибки (пустая data),
    // на данном этапе будем считать все ошибки за 404
    // https://github.com/apollographql/apollo-client/issues/4138
    // https://github.com/apollographql/apollo-client/issues/4644
    // https://github.com/apollographql/react-apollo/pull/2753
    // Другие временные решения:
    // 1) fetchPolicy: 'no-cache', но оно производит запрос на стороне клиента
    // 2) подкладывать ошибки при SSR в window.__ERROR_TYPE__, придется каждый раз проверять их там на клиенте

    if (!loading && (!data || !data.product)) {
        return <NotFound />;
    }

    return <ProductDetail error={error} loading={loading} product={product} productId={productId} />;
};

export default productDetailFetchDataHoc;
